import { Button } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Tooltip from "@mui/material/Tooltip";
import React, { useContext, useEffect, useRef, useState } from "react";
import { InfoCircle } from "react-bootstrap-icons";
import "../../../../Tooltip/styles/tooltip.css";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import { FormContext } from "../context/FormProvider.jsx";
import "../styles/form-container.css";
import "./styles/new-text.css";

/**
 * NewText Component
 * @description A component that renders a mui outlined input made for name/company inputs.
 * @param {Object} props - The component props.
 * @param {string} props.idProp - The id of the text field.
 * @param {string} props.label - The label for the text field.
 * @param {string} props.val - The value of the text field.
 * @param {Object} props.state - The slide state object.
 * @param {Function} props.setState - The slide state setter function.
 * @param {boolean} props.req - A boolean indicating if the field is required.
 * @param {Object} props.debouncedSave - The debounced save function.
 * @param {Function} props.setIsLoading - The loading state setter function.
 * @param {Array} props.autoData - The array of names or companies.
 * @param {boolean} props.disabledInput - A boolean indicating if the input is disabled.
 * @param {string} props.disabledInputMessage - The message to display when the input is disabled.
 * @param {Object} props.invalidData - The invalid data object.
 * @returns {JSX.Element} The rendered TextField component.
 */
const LaydownCalc = ({
  idProp,
  label,
  val,
  state,
  setState,
  req,
  debouncedSave,
  setIsLoading,
  autoData,
  type,
  disabledInput,
  disabledInputMessage,
  invalidData,
}) => {
  const textRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [openHelper, setOpenHelper] = useState(false);
  const [openCalculator, setOpenCalculator] = useState({
    totAcr: false,
    distAcr: false,
  });
  const [showPlaceholder, setShowPlaceholder] = useState(false);
  const [placeholder, setPlaceholder] = useState("");
  const [adjustMargin, setAdjustMargin] = useState(false);
  const { requestStatus, handleInputFocus, handleInputDisable } =
    useContext(FormContext);
  const { capitalizeNameAfterSpace, userId } = useContext(DashboardContext);

  useEffect(() => {
    if (req && openHelper) {
      setAdjustMargin(true);
    } else {
      setAdjustMargin(false);
    }
  }, [req, openHelper]);

  const handleUpdateLaydownSize = (e) => {
    // Remove any non-numeric characters/ "." from the input value
    const updatedValue = e.target.value.replace(/[^0-9.]/g, "");
    setState({
      ...state,
      layDownSize: updatedValue,
    });
  };

  const handleUpdateDistAcr = (e) => {
    let newDistAcr = (+state.distAcr + +state.layDownSize).toString();

    let newState = {
      ...state,
      layDownSize: "",
      distAcr: newDistAcr,
      layDownYardDist: "Yes",
    };

    setState(newState);

    if (debouncedSave && setIsLoading) {
      setIsLoading(true);
      debouncedSave.mutate(newState);
    }
  };

  return (
    <div className="laydown-container">
      <div className={`text-info-container ${type}-info-container`}>
        <FormControl
          variant="outlined"
          id="text-form"
          classes={{ root: "form-text-root" }}
          className={`${type}-form-control ${
            idProp === "portableTankCapacity" && +val >= 1320
              ? "spccAlert"
              : requestStatus === "RV" &&
                disabledInput === false &&
                invalidData &&
                (val === invalidData["initialValue"] || val === "")
              ? "revise-input"
              : requestStatus === "RV" &&
                disabledInput === false &&
                invalidData &&
                val !== invalidData["initialValue"] &&
                val !== ""
              ? "revised-input"
              : handleInputDisable(idProp, state, disabledInput, userId) ===
                true
              ? "disable-text"
              : req
              ? "required"
              : ""
          }`}
        >
          <InputLabel
            id="text-field-label"
            htmlFor="text-input"
            classes={{ shrink: "text-field-label-shrink" }}
            className={`${idProp}-text-field-label`}
          >
            Estimated Yard Size
          </InputLabel>
          <OutlinedInput
            type="text"
            id="text-field"
            className={`${idProp}-input`}
            ref={textRef}
            value={val}
            onChange={handleUpdateLaydownSize}
            onFocus={() => handleInputFocus(textRef, setOpenHelper)}
            onSelect={() => {
              if (state[`${idProp}`] === "") {
                setOpen(true);
              } else {
                setOpen(false);
              }
            }}
            disabled={
              handleInputDisable(idProp, state, disabledInput, userId) === true
                ? true
                : false
            }
            autoComplete="off"
            autoCapitalize="words"
            classes={{
              root: "text-field-root",
              notchedOutline: "text-notchedOutline",
              input: "text-field-input",
            }}
          />
        </FormControl>
        <input
          key={`${idProp}-placeholder`}
          className={"predictive-placeholder"}
          type="text"
          autoComplete="off"
          id={`${type}-placeholder`}
          value={placeholder}
          disabled={true}
        />
        <Tooltip
          id="tf-tooltip"
          classes={{
            tooltip: "text-field-tooltip",
            popper: "text-field-popper",
            arrow: "text-field-arrow",
          }}
          title={
            "Input the estimated yard size here and then click the add button to update the disturbed acreage"
          }
          placement={"top"}
          arrow
          open={open}
          sx={{
            backgroundColor: "rgb(0, 0, 0, 0.8) !important",
          }}
        >
          <InfoCircle
            className="text-info-circ"
            id={`info-${idProp}`}
            color="white"
            size={30}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          />
        </Tooltip>
      </div>
      <Button
        id="add-btn"
        variant="contained"
        className="add-btn"
        onClick={
          !state.layDownSize || state.layDownSize !== ""
            ? handleUpdateDistAcr
            : null
        }
        disabled={!state.layDownSize || state.layDownSize === ""}
      >
        Add
      </Button>
    </div>
  );
};
export default LaydownCalc;
